import React, { useState, useEffect } from 'react';
import { ListPlus } from 'lucide-react';
import BotaoFechar, { useEscFecha } from './BotaoFechar';
import TagInput from './TagInput';
import DateInput from './DateInput';
import MonthInput from './MonthInput';
import MoneyInput from './MoneyInput';
import { todayBR } from '../lib/format';
import { colorOptionStyle } from '../lib/colorTint';

export default function TransactionFormModal({ initial, mode = 'create', pots, accounts, entities, sources, tags, onClose, onSave, onCreateTag, applyToAllFn }) {
  const today = todayBR();
  const blank = {
    type: 'expense', amount: '', transaction_date: today,
    reference_month: '', description: '', notes: '',
    pot_exit_id: null, pot_entry_id: null, account_exit_id: null, account_entry_id: null,
    entity_id: null, revenue_source_id: null, tag_ids: [],
  };
  const [form, setForm] = useState(blank);
  const [submitting, setSubmitting] = useState(false);
  useEscFecha(onClose);

  useEffect(() => {
    if (initial) {
      setForm({
        ...blank,
        ...initial,
        amount: initial.amount,
        tag_ids: initial.tags?.map((t) => t.id) || initial.tag_ids || [],
      });
    } else {
      setForm(blank);
    }
    // eslint-disable-next-line
  }, [initial?.id]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const intOrNull = (v) => v ? parseInt(v) : null;
  const setTagIds = (ids) => setForm((f) => ({ ...f, tag_ids: ids }));

  const titleText = mode === 'edit' ? 'Editar Transação' : mode === 'duplicate' ? 'Duplicar Transação' : 'Nova Transação';
  const submitLabel = mode === 'edit' ? 'Atualizar Transação' : 'Criar Transação';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSave({ ...form, amount: parseFloat(form.amount) || 0 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">{titleText}</h3>
          <BotaoFechar onClose={onClose} testid="tx-form-fechar" />
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 pb-2 space-y-4">
          {/* Description - full width */}
          <Field>
            <input
              value={form.description || ''}
              onChange={(e) => upd('description', e.target.value)}
              placeholder="Descrição da transação"
              className="input-md"
              autoFocus
            />
          </Field>

          {/* Date + Reference Month */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Data da Transação">
              <DateInput value={form.transaction_date} onChange={(v) => upd('transaction_date', v)} required />
            </Field>
            <Field label="Mês de Referência (opcional)">
              <MonthInput value={form.reference_month || ''} onChange={(v) => upd('reference_month', v)} />
            </Field>
          </div>

          {/* Type + Amount */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Tipo">
              <select value={form.type} onChange={(e) => upd('type', e.target.value)} className="input-md">
                <option value="expense">Despesa</option>
                <option value="income">Receita</option>
                <option value="transfer">Transferência</option>
              </select>
            </Field>
            <Field label={<>Valor <span className="text-rose-500">*</span></>}>
              <MoneyInput value={form.amount} onChange={(v) => upd('amount', v)} className="input-md" placeholder="0,00" required />
            </Field>
          </div>

          {/* Notes + Tags */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Observações (opcional)">
              <input value={form.notes || ''} onChange={(e) => upd('notes', e.target.value)} placeholder="Observações adicionais" className="input-md" />
            </Field>
            <Field label="Tags (opcional)" applyAll={applyToAllFn ? () => applyToAllFn('tag_ids', form.tag_ids) : null} testId="apply-all-tags">
              <TagInput
                tags={tags || []}
                selectedIds={form.tag_ids}
                onChange={setTagIds}
                onCreateTag={onCreateTag}
              />
            </Field>
          </div>

          {/* Entrada / Saída box */}
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-slate-50/50 dark:bg-slate-900/30">
            <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">Configurações de Entrada e Saída</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Configure os potes e contas envolvidos. Use campos de saída para despesas e entrada para receitas.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectField label="Pote Saída" value={form.pot_exit_id} onChange={(v) => upd('pot_exit_id', intOrNull(v))} options={pots} placeholder="Nenhum" applyAll={applyToAllFn ? () => applyToAllFn('pot_exit_id', form.pot_exit_id) : null} testId="apply-all-pot-exit" />
              <SelectField label="Pote Entrada" value={form.pot_entry_id} onChange={(v) => upd('pot_entry_id', intOrNull(v))} options={pots} placeholder="Nenhum" applyAll={applyToAllFn ? () => applyToAllFn('pot_entry_id', form.pot_entry_id) : null} testId="apply-all-pot-entry" />
              <SelectField label="Conta Saída" value={form.account_exit_id} onChange={(v) => upd('account_exit_id', intOrNull(v))} options={accounts} placeholder="Nenhuma" applyAll={applyToAllFn ? () => applyToAllFn('account_exit_id', form.account_exit_id) : null} testId="apply-all-account-exit" />
              <SelectField label="Conta Entrada" value={form.account_entry_id} onChange={(v) => upd('account_entry_id', intOrNull(v))} options={accounts} placeholder="Nenhuma" applyAll={applyToAllFn ? () => applyToAllFn('account_entry_id', form.account_entry_id) : null} testId="apply-all-account-entry" />
            </div>
          </div>

          {/* Revenue source + Entity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectField label="Fonte de Receita (opcional)" value={form.revenue_source_id} onChange={(v) => upd('revenue_source_id', intOrNull(v))} options={sources} placeholder="Nenhuma fonte" applyAll={applyToAllFn ? () => applyToAllFn('revenue_source_id', form.revenue_source_id) : null} testId="apply-all-revenue-source" />
            <SelectField label="Pessoa/Entidade (opcional)" value={form.entity_id} onChange={(v) => upd('entity_id', intOrNull(v))} options={entities} placeholder="Nenhuma entidade" applyAll={applyToAllFn ? () => applyToAllFn('entity_id', form.entity_id) : null} testId="apply-all-entity" />
          </div>
        </form>

        <div className="flex gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <div className="flex-1" />
          <button type="button" onClick={onClose}
            className="px-5 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 font-medium">
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting}
            className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-500 hover:from-indigo-700 hover:to-blue-600 text-white rounded-lg shadow-md font-medium disabled:opacity-60">
            {submitting ? 'Salvando...' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, applyAll, testId }) {
  return (
    <div>
      {label && (
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-semibold text-slate-800 dark:text-slate-200">{label}</label>
          {applyAll && (
            <button type="button" onClick={applyAll} data-testid={testId}
              title="Aplicar este valor a todas as outras linhas"
              className="text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 font-medium">
              <ListPlus className="w-3 h-3" /> Aplicar a todas
            </button>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder = '-', applyAll, testId }) {
  return (
    <Field label={label} applyAll={applyAll} testId={testId}>
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="input-md">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.id} value={o.id} style={colorOptionStyle(o.color)}>{o.name}</option>)}
      </select>
    </Field>
  );
}
